// Correção automática de ortografia e acentos (Pedro, 30/10/2026: «conforme eu for dando espaço ele corrige a palavra»,
// como o Gboard). Ao digitar espaço ou pontuação, a palavra que acabou é corrigida se estiver errada; Backspace (ou
// Ctrl+Z) logo depois desfaz, e a palavra desfeita não é corrigida de novo neste texto. Só no computador: no celular e no
// tablet o teclado já corrige, e corrigir de novo atrapalha. Na dúvida, não corrige.
//
// O dicionário (public/dicionario/pt-br.txt: palavra e quantas vezes aparece, da lista de frequência pt_br do
// FrequencyWords, CC-BY-SA-4.0) só baixa quando um campo recebe o foco. A escolha é pela frequência:
// - acento: a forma acentuada, se for 20× mais comum que a digitada («nao» → «não»; «esta» fica: «está» é só 9×);
// - uma letra: em palavra de 4+ letras que quase não existe, a mais comum a uma letra de distância, com folga (em 3
//   letras, só duas vizinhas invertidas: «qeu» → «que»); a achada sem acento ganha o dela («tambme» → «também»).
// A palavra rara que é erro de uma muito mais comum («memso», 58 vezes nas legendas) já sai do dicionário
// (~/ferramentas/frontend-vnz/gerar-dicionario-ptbr.py), senão contaria como palavra de verdade.
import { useEffect, useLayoutEffect, useRef, type FocusEvent, type KeyboardEvent, type RefObject } from "react";

interface Dicionario {
  n: Map<string, number>;
  /** A forma sem acento → as palavras do dicionário que ficam assim («nao» → «não»). */
  porSemAcento: Map<string, string[]>;
}

const SEPARADORES = new Set([" ", ",", ".", "!", "?", ";", ":"]);
// Gíria e abreviação comum ficam como estão.
const GIRIAS = new Set(["vc", "vcs", "pq", "msm", "tb", "tbm", "blz", "obg", "vlw", "flw", "pfv", "pfvr", "hj", "oq", "cmg", "ctz", "mt", "mto", "td", "tds", "sla", "nd", "qnd", "qdo", "dps", "agr", "bjs", "bj", "abs", "att", "tmj", "sqn", "pdc", "cvs", "vdd", "fds", "pls", "plz", "ok", "q", "api", "ads"]);
const RISADA = /^(k{2,}|(ha)+h?|(he)+h?|(rs)+|a?(ha)+)$/;
const LETRAS = "abcdefghijklmnopqrstuvwxyzáàâãéêíóôõúüç";
const ACENTO_RAZAO = 20;
const ACENTO_MIN = 100;
const UMA_LETRA_MIN = 1000;
// Em 3 letras, só as muito comuns («que», «mas», «por»…): abaixo disso vêm nomes e inglês das legendas («day», «ron»).
const TRES_LETRAS_MIN = 100_000;
const UMA_LETRA_FOLGA = 5;
const PALAVRA_DE_VERDADE = 50;

const semAcento = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "");

export function montarDicionario(texto: string): Dicionario {
  const n = new Map<string, number>();
  const porSemAcento = new Map<string, string[]>();
  for (const linha of texto.split("\n")) {
    if (!linha || linha.startsWith("#")) continue;
    const i = linha.indexOf(" ");
    const p = linha.slice(0, i);
    n.set(p, Number(linha.slice(i + 1)) || 0);
    const k = semAcento(p);
    const lista = porSemAcento.get(k);
    if (lista) lista.push(p);
    else porSemAcento.set(k, [p]);
  }
  return { n, porSemAcento };
}

let dicionario: Dicionario | null = null;
let carregando: Promise<Dicionario | null> | null = null;
export function carregarDicionario(): Promise<Dicionario | null> {
  if (dicionario) return Promise.resolve(dicionario);
  carregando ??= fetch(`${import.meta.env.BASE_URL}dicionario/pt-br.txt`)
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`dicionário ${r.status}`))))
    .then((texto) => {
      dicionario = montarDicionario(texto);
      return dicionario;
    })
    .catch(() => {
      carregando = null; // tenta de novo no próximo foco
      return null;
    });
  return carregando;
}

// As palavras a uma letra de distância (tirar, trocar, pôr ou inverter duas vizinhas); em 3 letras, só inverter.
function vizinhas(w: string): string[] {
  const r: string[] = [];
  for (let i = 0; i <= w.length; i++) {
    const a = w.slice(0, i), b = w.slice(i);
    if (b.length > 1) r.push(a + b[1] + b[0] + b.slice(2));
    if (w.length < 4) continue;
    if (b) r.push(a + b.slice(1));
    for (const l of LETRAS) {
      if (b) r.push(a + l + b.slice(1));
      r.push(a + l + b);
    }
  }
  return r;
}

const comMaiuscula = (p: string) => p.charAt(0).toLocaleUpperCase("pt-BR") + p.slice(1);

// A forma acentuada da palavra, quando ela é bem mais comum («nao» → «não»).
function comAcento(p: string, dic: Dicionario): string | null {
  const n = (x: string) => dic.n.get(x) ?? 0;
  let acentuada: string | null = null;
  for (const v of dic.porSemAcento.get(semAcento(p)) ?? []) if (v !== p && (!acentuada || n(v) > n(acentuada))) acentuada = v;
  return acentuada && n(acentuada) >= ACENTO_MIN && n(acentuada) >= ACENTO_RAZAO * Math.max(1, n(p)) ? acentuada : null;
}

/** A palavra corrigida, ou null para deixar como está. */
export function corrigir(palavra: string, dic: Dicionario, ignorar: Set<string>): string | null {
  if (!/^\p{L}+$/u.test(palavra)) return null;
  const baixa = palavra.toLocaleLowerCase("pt-BR");
  if (ignorar.has(baixa) || GIRIAS.has(baixa) || RISADA.test(baixa)) return null;
  // TUDO EM MAIÚSCULA e caixa misturada ficam; só a primeira maiúscula passa (e só acento: pode ser nome).
  if (palavra.length > 1 && palavra === palavra.toLocaleUpperCase("pt-BR")) return null;
  const capitalizada = palavra !== baixa;
  if (capitalizada && palavra !== comMaiuscula(baixa)) return null;
  const n = (p: string) => dic.n.get(p) ?? 0;
  const digitada = n(baixa);
  const acentuada = comAcento(baixa, dic);
  if (acentuada) return capitalizada ? comMaiuscula(acentuada) : acentuada;
  if (capitalizada || baixa.length < 3 || digitada >= PALAVRA_DE_VERDADE) return null;
  let melhor: string | null = null, n1 = 0, n2 = 0;
  for (const v of new Set(vizinhas(baixa))) {
    const c = n(v);
    if (c > n1) {
      n2 = n1;
      n1 = c;
      melhor = v;
    } else if (c > n2) n2 = c;
  }
  if (!melhor || n1 < (baixa.length < 4 ? TRES_LETRAS_MIN : UMA_LETRA_MIN) || n1 < UMA_LETRA_FOLGA * n2) return null;
  return melhor === semAcento(melhor) ? (comAcento(melhor, dic) ?? melhor) : melhor;
}

/**
 * Liga a correção num campo de texto controlado: espalhe o que volta no <textarea> e chame o `onKeyDown` dele antes do
 * do campo (se ele corrigiu ou desfez, `e.defaultPrevented` fica true).
 */
export function useCorrecao(valor: string, setValor: (v: string) => void, campo: RefObject<HTMLTextAreaElement | null>) {
  const ultima = useRef<{ inicio: number; original: string; corrigida: string; separador: string; fim: number } | null>(null);
  const ignorar = useRef(new Set<string>());
  // O cursor depois da troca vai assim que o React grava o texto, antes da próxima tecla. Com o requestAnimationFrame, a
  // letra digitada antes do quadro seguinte ficava atrás do cursor e ia parar no fim («botao de» → «botão e…d»).
  const cursor = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (cursor.current === null) return;
    campo.current?.setSelectionRange(cursor.current, cursor.current);
    cursor.current = null;
  }, [valor, campo]);
  // Texto novo (enviado ou apagado): a palavra desfeita volta a ser corrigida.
  useEffect(() => {
    if (!valor) ignorar.current.clear();
  }, [valor]);
  const noComputador = () => typeof window !== "undefined" && !!window.matchMedia?.("(pointer: fine)").matches;
  const aplicar = (novo: string, pos: number) => {
    cursor.current = pos;
    setValor(novo);
  };
  return {
    spellCheck: true,
    lang: "pt-BR",
    onFocus: (_e: FocusEvent<HTMLTextAreaElement>) => {
      if (noComputador()) void carregarDicionario();
    },
    onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!noComputador() || e.nativeEvent.isComposing || e.keyCode === 229) return;
      // Só a tecla modificadora (o Shift antes do Backspace) não conta como «outra tecla».
      if (["Shift", "Control", "Alt", "Meta", "CapsLock"].includes(e.key)) return;
      const el = e.currentTarget;
      const u = ultima.current;
      ultima.current = null;
      const desfazer =
        (e.key === "Backspace" && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) ||
        ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "z");
      if (desfazer && u && el.selectionStart === el.selectionEnd && el.selectionStart === u.fim && el.value.slice(u.inicio, u.fim) === u.corrigida + u.separador) {
        e.preventDefault();
        ignorar.current.add(u.original.toLocaleLowerCase("pt-BR"));
        aplicar(el.value.slice(0, u.inicio) + u.original + u.separador + el.value.slice(u.fim), u.inicio + u.original.length + u.separador.length);
        return;
      }
      if (!SEPARADORES.has(e.key) || e.ctrlKey || e.metaKey || e.altKey || !dicionario || el.selectionStart !== el.selectionEnd) return;
      const pos = el.selectionStart;
      // A palavra que acabou: o pedaço sem espaço antes do cursor, só de letras (abre-parêntese e aspas na frente passam).
      const pedaco = /\S+$/.exec(el.value.slice(0, pos))?.[0];
      const p = pedaco ? /^[("'“‘«]*(\p{L}+)$/u.exec(pedaco)?.[1] : undefined;
      if (!p) return;
      const corrigida = corrigir(p, dicionario, ignorar.current);
      if (!corrigida || corrigida === p) return;
      e.preventDefault();
      const inicio = pos - p.length;
      const fim = inicio + corrigida.length + e.key.length;
      ultima.current = { inicio, original: p, corrigida, separador: e.key, fim };
      aplicar(el.value.slice(0, inicio) + corrigida + e.key + el.value.slice(pos), fim);
    },
  };
}
